import { useEffect, useState } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ExecutionModeField } from './ExecutionModeField';
import { RunConditionsSection } from './RunConditions/RunConditionsSection';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/Domain/components/Shared/Accordion/Accordion';
import { HttpActionForm } from '@/Domain/hooks/Action/ApiAction/useHttpRequestActionFormV2.hook';

type Props = {
  form: UseFormReturn<HttpActionForm>;
};

export const WHEN_TO_RUN_SECTION_ID = 'execution_mode';

export const WhenToRunSection = ({ form }: Props) => {
  const { t } = useTranslation('actions_page', { keyPrefix: 'sheet.form.execution_mode' });
  const executionMode = useWatch({ control: form.control, name: 'execution_mode' });

  // Collapsed by default: almost every Action keeps "AI decides". One that does
  // not is opened, so its mode and the conditions it needs stay visible.
  const [openItem, setOpenItem] = useState('');
  useEffect(() => {
    if (executionMode && executionMode !== 'ai_decides') setOpenItem(WHEN_TO_RUN_SECTION_ID);
  }, [executionMode]);

  return (
    <div id={WHEN_TO_RUN_SECTION_ID}>
      <Accordion
        type="single"
        collapsible
        value={openItem}
        onValueChange={setOpenItem}
        className="w-full"
        data-testid="http-request-action-when-to-run-accordion"
      >
        <AccordionItem value={WHEN_TO_RUN_SECTION_ID} className="border-b-0">
          <AccordionTrigger
            className="cursor-pointer px-1 py-3 font-medium text-foreground"
            data-testid="http-request-action-when-to-run-trigger"
          >
            <div className="flex items-center gap-3">
              {t('label')}
              <span
                className="text-sm font-normal text-muted-foreground"
                data-testid="http-request-action-when-to-run-summary"
              >
                {t(`${executionMode ?? 'ai_decides'}.label`)}
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-7 pb-0 pt-2">
            <ExecutionModeField />
            {executionMode === 'when_metadata_matches' && (
              <RunConditionsSection form={form} isTrigger />
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
